import { generateClient } from 'aws-amplify/data'
import type { Schema } from '../../amplify/data/resource'

const dataClient = generateClient<Schema>()

export interface Client {
  id: string
  name: string
  description?: string
  contactEmail?: string
  contactPhone?: string
  isActive: boolean
  createdBy: string
  createdAt?: string
  updatedAt?: string
}

export interface Location {
  id: string
  clientId: string
  name: string
  address?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
  contactName?: string
  contactPhone?: string
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

export interface CreateClientRequest {
  name: string
  description?: string
  contactEmail?: string
  contactPhone?: string
}

export interface UpdateClientRequest {
  id: string
  name?: string
  description?: string
  contactEmail?: string
  contactPhone?: string
  isActive?: boolean
}

export interface CreateLocationRequest {
  clientId: string
  name: string
  address?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
  contactName?: string
  contactPhone?: string
}

export interface UpdateLocationRequest {
  id: string
  name?: string
  address?: string
  city?: string
  state?: string
  country?: string
  postalCode?: string
  contactName?: string
  contactPhone?: string
  isActive?: boolean
}

class ClientService {
  /**
   * List all clients
   */
  async listClients(): Promise<Client[]> {
    try {
      const { data, errors } = await dataClient.models.Client.list()
      
      if (errors) {
        console.error('Error listing clients:', errors)
        throw new Error('Failed to list clients')
      }

      return data as unknown as Client[]
    } catch (error) {
      console.error('Error listing clients:', error)
      throw error
    }
  }

  /**
   * Get a client by ID
   */
  async getClient(id: string): Promise<Client | null> {
    try {
      const { data, errors } = await dataClient.models.Client.get({ id })
      
      if (errors) {
        console.error('Error getting client:', errors)
        throw new Error('Failed to get client')
      }

      return data as unknown as Client
    } catch (error) {
      console.error('Error getting client:', error)
      throw error
    }
  }

  /**
   * Create a new client
   */
  async createClient(request: CreateClientRequest, createdBy: string): Promise<Client> {
    try {
      const { data, errors } = await dataClient.models.Client.create({
        name: request.name,
        description: request.description,
        contactEmail: request.contactEmail,
        contactPhone: request.contactPhone,
        isActive: true,
        createdBy,
      })
      
      if (errors) {
        console.error('Error creating client:', errors)
        throw new Error('Failed to create client')
      }

      return data as unknown as Client
    } catch (error) {
      console.error('Error creating client:', error)
      throw error
    }
  }

  /**
   * Update a client
   */
  async updateClient(request: UpdateClientRequest): Promise<Client> {
    try {
      const updateData: { id: string; [key: string]: string | boolean } = { id: request.id }
      
      if (request.name !== undefined) updateData.name = request.name
      if (request.description !== undefined) updateData.description = request.description
      if (request.contactEmail !== undefined) updateData.contactEmail = request.contactEmail
      if (request.contactPhone !== undefined) updateData.contactPhone = request.contactPhone
      if (request.isActive !== undefined) updateData.isActive = request.isActive

      const { data, errors } = await dataClient.models.Client.update(updateData as Parameters<typeof dataClient.models.Client.update>[0])
      
      if (errors) {
        console.error('Error updating client:', errors)
        throw new Error('Failed to update client')
      }

      return data as unknown as Client
    } catch (error) {
      console.error('Error updating client:', error)
      throw error
    }
  }

  /**
   * Delete a client
   */
  async deleteClient(id: string): Promise<void> {
    try {
      const { errors } = await dataClient.models.Client.delete({ id })
      
      if (errors) {
        console.error('Error deleting client:', errors)
        throw new Error('Failed to delete client')
      }
    } catch (error) {
      console.error('Error deleting client:', error)
      throw error
    }
  }

  /**
   * List locations for a client
   */
  async listClientLocations(clientId: string): Promise<Location[]> {
    try {
      const { data, errors } = await dataClient.models.Location.list({
        filter: { clientId: { eq: clientId } }
      })
      
      if (errors) {
        console.error('Error listing client locations:', errors)
        throw new Error('Failed to list client locations')
      }

      return data as unknown as Location[]
    } catch (error) {
      console.error('Error listing client locations:', error)
      throw error
    }
  }

  /**
   * Create a new location
   */
  async createLocation(request: CreateLocationRequest): Promise<Location> {
    try {
      const { data, errors } = await dataClient.models.Location.create({
        clientId: request.clientId,
        name: request.name,
        address: request.address,
        city: request.city,
        state: request.state,
        country: request.country,
        postalCode: request.postalCode,
        contactName: request.contactName,
        contactPhone: request.contactPhone,
        isActive: true,
      })
      
      if (errors) {
        console.error('Error creating location:', errors)
        throw new Error('Failed to create location')
      }

      return data as unknown as Location
    } catch (error) {
      console.error('Error creating location:', error)
      throw error
    }
  }

  /**
   * Update a location
   */
  async updateLocation(request: UpdateLocationRequest): Promise<Location> {
    try {
      const updateData: { id: string; [key: string]: string | boolean } = { id: request.id }
      
      if (request.name !== undefined) updateData.name = request.name
      if (request.address !== undefined) updateData.address = request.address
      if (request.city !== undefined) updateData.city = request.city
      if (request.state !== undefined) updateData.state = request.state
      if (request.country !== undefined) updateData.country = request.country
      if (request.postalCode !== undefined) updateData.postalCode = request.postalCode
      if (request.contactName !== undefined) updateData.contactName = request.contactName
      if (request.contactPhone !== undefined) updateData.contactPhone = request.contactPhone
      if (request.isActive !== undefined) updateData.isActive = request.isActive

      const { data, errors } = await dataClient.models.Location.update(updateData as Parameters<typeof dataClient.models.Location.update>[0])
      
      if (errors) {
        console.error('Error updating location:', errors)
        throw new Error('Failed to update location')
      }

      return data as unknown as Location
    } catch (error) {
      console.error('Error updating location:', error)
      throw error
    }
  }

  /**
   * Delete a location
   */
  async deleteLocation(id: string): Promise<void> {
    try {
      const { errors } = await dataClient.models.Location.delete({ id })
      
      if (errors) {
        console.error('Error deleting location:', errors)
        throw new Error('Failed to delete location')
      }
    } catch (error) {
      console.error('Error deleting location:', error)
      throw error
    }
  }

  /**
   * Get devices for a client (all locations)
   */
  async getClientDevices(clientId: string) {
    try {
      const { data, errors } = await dataClient.models.Device.list({
        filter: { clientId: { eq: clientId } }
      })
      
      if (errors) {
        console.error('Error getting client devices:', errors)
        throw new Error('Failed to get client devices')
      }

      return data
    } catch (error) {
      console.error('Error getting client devices:', error)
      throw error
    }
  }

  /**
   * Get devices for a specific location
   */
  async getLocationDevices(locationId: string) {
    try {
      const { data, errors } = await dataClient.models.Device.list({
        filter: { locationId: { eq: locationId } }
      })
      
      if (errors) {
        console.error('Error getting location devices:', errors)
        throw new Error('Failed to get location devices')
      }

      return data
    } catch (error) {
      console.error('Error getting location devices:', error)
      throw error
    }
  }
}

export default new ClientService()
